/**
 * Application state management
 * Centralizes all global state variables
 */

import { REFRESH_INTERVALS } from '../config.js';

// Application state
const state = {
  // Configuration from server
  accessToken: null,
  deviceID: null,
  csrfToken: null,
  vacuumBridge: null, // { bridgeUrl, token } or null when not configured
  wsid: crypto.randomUUID(),

  // WebSocket connection
  socket: null,

  // Chart state
  clickedSensor: null,
  windObs: [], // Will be initialized with cWind value

  // Timers and intervals
  lightningCheck: null,
  taskCheck: null,

  // Data
  warnings: null,

  // Degradation status
  degradationStatus: null,
  systemStatus: 'healthy',

  // Temperature display state
  tempUnit: 'F', // Current display unit: 'F' or 'C'
  currentTempF: null,
  currentTempC: null,

  // Latest sensor health summary (offline / low battery counts)
  sensorHealth: null,

  // Tracking when various data sources were last checked
  lastCheck: {},
  checkTimes: {
    'Radar': REFRESH_INTERVALS.RADAR,
    'Warnings': REFRESH_INTERVALS.WARNINGS,
    'Lightning': REFRESH_INTERVALS.LIGHTNING,
    'Weather': REFRESH_INTERVALS.WEATHER,
    'Task': REFRESH_INTERVALS.TASK,
    'Forecast': REFRESH_INTERVALS.FORECAST,
    'Sensors': REFRESH_INTERVALS.SENSORS,
    'Chore': REFRESH_INTERVALS.CHORE,
    'Appliances': REFRESH_INTERVALS.APPLIANCES,
  },

  // Chart instances (will be populated when charts are created)
  charts: {
    wind: null,
    precip: null,
    precipDuration: null,
    uv: null,
    dp: null,
    timeSeries: null,
    timeSeriesSensors: null,
  },
};

// Getters
export function getAccessToken() {
  return state.accessToken;
}

export function getDeviceID() {
  return state.deviceID;
}

export function getVacuumBridge() {
  return state.vacuumBridge;
}

export function getCsrfToken() {
  return state.csrfToken;
}

export function getWsid() {
  return state.wsid;
}

export function getSocket() {
  return state.socket;
}

export function getWindObs() {
  return state.windObs;
}

export function getLastCheck() {
  return state.lastCheck;
}

export function getCheckTimes() {
  return state.checkTimes;
}

export function getChart(chartName) {
  return state.charts[chartName];
}

// Setters
export function setAccessToken(token) {
  state.accessToken = token;
}

export function setDeviceID(id) {
  state.deviceID = id;
}

export function setVacuumBridge(bridge) {
  state.vacuumBridge = bridge;
}

export function setCsrfToken(token) {
  state.csrfToken = token;
}

export function setSocket(socket) {
  state.socket = socket;
}

export function setWindObs(obs) {
  state.windObs = obs;
}

export function addWindOb(ob) {
  state.windObs.push(ob);
}

export function setWarnings(warnings) {
  state.warnings = warnings;
}

export function setLastCheck(product, time) {
  state.lastCheck[product] = time;
}

export function setSensorHealth(health) {
  state.sensorHealth = health;
}

export function getSensorHealth() {
  return state.sensorHealth;
}

export function setChart(chartName, chart) {
  state.charts[chartName] = chart;
}

export function setDegradationStatus(status) {
  state.degradationStatus = status;
  state.systemStatus = status.status || 'healthy';
}

export function getDegradationStatus() {
  return state.degradationStatus;
}

export function getSystemStatus() {
  return state.systemStatus;
}

export function getTempUnit() {
  return state.tempUnit;
}

export function setTempUnit(unit) {
  state.tempUnit = unit;
}

export function getCurrentTempF() {
  return state.currentTempF;
}

export function setCurrentTempF(temp) {
  state.currentTempF = temp;
}

export function getCurrentTempC() {
  return state.currentTempC;
}

export function setCurrentTempC(temp) {
  state.currentTempC = temp;
}

// Export the entire state for debugging (read-only)
export function getState() {
  return { ...state };
}
